import { Fragment, useEffect, useRef, useState } from "react";
import Connector2 from "./Connector2";
import Connector3 from "./Connector3";
import MobileTree from "./MobileTree";
import "./VanshavaliForm.scss";

const HONEYPOT_STYLE = {
  position: "absolute",
  left: "-9999px",
  width: 1,
  height: 1,
  opacity: 0,
  pointerEvents: "none",
};

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(max-width: 767px)").matches
      : false
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const onChange = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return isMobile;
}

const ArrowDown = () => (
  <svg width="12" height="22" viewBox="0 0 12 22" style={{ display: "inline-block" }}>
    <line x1="6" y1="0" x2="6" y2="14" stroke="#aaa" strokeWidth="1.5" />
    <polygon points="1,14 6,22 11,14" fill="#aaa" />
  </svg>
);

const ArrowLeft = () => (
  <div className="al">
    <div className="lbl" aria-hidden style={{ visibility: "hidden" }}>.</div>
    <div className="al-bar">
      <svg width="8" height="10" viewBox="0 0 8 10">
        <polygon points="8,0 0,5 8,10" fill="#aaa" />
      </svg>
      <div style={{ flex: 1, height: 1.5, background: "#aaa" }} />
    </div>
  </div>
);

const ArrowRight = () => (
  <div className="ar">
    <div className="lbl" aria-hidden style={{ visibility: "hidden" }}>.</div>
    <div className="al-bar">
      <div style={{ flex: 1, height: 1.5, background: "#aaa" }} />
      <svg width="8" height="10" viewBox="0 0 8 10">
        <polygon points="0,0 8,5 0,10" fill="#aaa" />
      </svg>
    </div>
  </div>
);

function VanshavaliForm({
  cityKey = "ahmedabad",
  cityConfig = { name: "Ahmedabad" },
  formNumber = "",
  onSubmit,
  submitAction = "/submit",
  submitting = false,
}) {
  const uncles = [
    { lbl: "Uncle", name: "uncle1", c1: "uncle1Child1", c2: "uncle1Child2" },
    { lbl: "Uncle", name: "uncle2", c1: "uncle2Child1", c2: "uncle2Child2" },
    { lbl: "Father", name: "fatherName", center: true },
    { lbl: "Uncle", name: "uncle3", c1: "uncle3Child1", c2: "uncle3Child2" },
    { lbl: "Uncle", name: "uncle4", c1: "uncle4Child1", c2: "uncle4Child2" },
  ];

  const bros = [
    { lbl: "Brother", name: "brother1", c1: "brother1Child1", c2: "brother1Child2" },
    { lbl: "Brother", name: "brother2", c1: "brother2Child1", c2: "brother2Child2" },
    { lbl: "Self / Member", name: "selfName", center: true },
    { lbl: "Brother", name: "brother3", c1: "brother3Child1", c2: "brother3Child2" },
    { lbl: "Brother", name: "brother4", c1: "brother4Child1", c2: "brother4Child2" },
  ];

  const [submitted, setSubmitted] = useState(false);
  const [localSubmitting, setLocalSubmitting] = useState(false);
  const isMobile = useIsMobile();
  const mountedAt = useRef(Date.now());
  const inFlight = useRef(false);

  // Reset the re-entry guard when submission completes (success or error).
  useEffect(() => {
    if (!submitting) {
      inFlight.current = false;
      setLocalSubmitting(false);
    }
  }, [submitting]);

  const isSubmitting = submitting || localSubmitting;

  const digitsOnly = (e) => {
    e.currentTarget.value = e.currentTarget.value.replace(/\D/g, "");
  };

  const handleSubmit = (e) => {
    const form = e.currentTarget;
    if (!form.checkValidity()) {
      e.preventDefault();
      setSubmitted(true);
      const firstInvalid = form.querySelector(":invalid");
      if (firstInvalid) {
        firstInvalid.scrollIntoView({ behavior: "smooth", block: "center" });
        firstInvalid.focus({ preventScroll: true });
      }
      return;
    }
    // Block re-entry — synchronous guard so a fast double-click can't
    // sneak through the gap before React re-renders submitting=true.
    if (inFlight.current) {
      e.preventDefault();
      return;
    }
    inFlight.current = true;
    if (onSubmit) {
      e.preventDefault();
      setLocalSubmitting(true);
      const data = Object.fromEntries(new FormData(form).entries());
      data._dwellMs = String(Date.now() - mountedAt.current);
      onSubmit(data);
    }
  };

  return (
    <div className="vf">
      <form
        action={submitAction}
        method="POST"
        className={`vf-form${submitted ? " submitted" : ""}`}
        onSubmit={handleSubmit}
        noValidate
      >
        <input type="hidden" name="cityKey" value={cityKey} />
        {/* Honeypot — bots auto-fill any text input; humans never see this. */}
        <input
          type="text"
          name="_hp"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          style={HONEYPOT_STYLE}
          defaultValue=""
        />

        {/* PERSONAL INFO */}
        <div className="vf-personal">
          <div className="vf-section-heading">Member Details</div>
          <div className="vf-grid">
            <div className="vf-field span-3">
              <label>Member Name<span className="req">*</span></label>
              <input type="text" name="memberName" required />
            </div>
            <div className="vf-field">
              <label>Native Place<span className="req">*</span></label>
              <select name="nativePlace" required defaultValue="">
                <option value="" disabled>Select</option>
                {["Bhadran", "Dharmaj", "Karamsad", "Nadiad", "Sojitra", "Vaso", "Savli"].map((v) => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
            </div>

            <div className="vf-field span-4">
              <label>Address</label>
              <textarea name="address" rows={2} />
            </div>

            <div className="vf-field">
              <label>Mobile<span className="req">*</span></label>
              <input
                type="tel"
                name="mobile"
                required
                inputMode="numeric"
                pattern="[0-9]{10}"
                maxLength={10}
                title="Enter a 10-digit mobile number"
                onInput={digitsOnly}
              />
            </div>
            <div className="vf-field span-2">
              <label>Email<span className="req">*</span></label>
              <input type="email" name="email" required />
            </div>
            <div className="vf-field">
              <label>Phone</label>
              <input
                type="tel"
                name="phone"
                inputMode="numeric"
                pattern="[0-9]{6,15}"
                title="Enter a valid phone number (digits only)"
                onInput={digitsOnly}
              />
            </div>

            <div className="vf-field">
              <label>Khadki / Thadiyu</label>
              <input type="text" name="khadki" />
            </div>
            <div className="vf-field">
              <label>Family</label>
              <input type="text" name="family" />
            </div>
            <div className="vf-field">
              <label>Mosal</label>
              <input type="text" name="mosal" />
            </div>
            <div className="vf-field">
              <label>Vanshavali Page #<span className="req">*</span></label>
              <input
                type="text"
                name="vanshavaliPage"
                required
                inputMode="numeric"
                pattern="[0-9]+"
                title="Numbers only"
                onInput={digitsOnly}
              />
            </div>
          </div>
        </div>

        {/* VANSHAVALI TREE — desktop / tablet */}
        {!isMobile && (
        <div className="vf-tree">
          {/* R1: Grandfather */}
          <div style={{ textAlign: "center", marginBottom: 0 }}>
            <div className="lbl-m">
              Grandfather's Name<span className="req">*</span>
            </div>
            <div style={{ display: "inline-block" }}>
              <input
                type="text"
                name="grandfatherName"
                required
                className="text-center mi"
                style={{ width: 192, fontWeight: 600 }}
                placeholder="Grandfather"
              />
            </div>
          </div>
          <div className="vf-arrow-down"><ArrowDown /></div>

          {/* R2+R3: Uncle/Father + children */}
          <div className="vf-row-flex">
            {uncles.map((u, i) => (
              <Fragment key={u.name}>
                {(i === 1 || i === 2) && <ArrowLeft />}
                {(i === 3 || i === 4) && <ArrowRight />}
                <div className="vf-col">
                  <div className={u.center ? "lbl-m" : "lbl"}>
                    {u.lbl}
                    {u.center && <span className="req">*</span>}
                  </div>
                  <input
                    type="text"
                    name={u.name}
                    required={u.center}
                    className={`text-center ${u.center ? "mi" : ""}`}
                    style={u.center ? { fontWeight: 600 } : undefined}
                    placeholder={u.lbl.replace(/[()]/g, "")}
                  />
                  {u.center ? (
                    <svg
                      width="12"
                      height="110"
                      viewBox="0 0 12 110"
                      style={{ display: "block", margin: "0 auto", marginBottom: -18, overflow: "visible" }}
                    >
                      <line x1="6" y1="0" x2="6" y2="128" stroke="#aaa" strokeWidth="1.5" />
                      <polygon points="1,128 6,136 11,128" fill="#aaa" />
                    </svg>
                  ) : (
                    <Connector2 name1={u.c1} name2={u.c2} />
                  )}
                </div>
              </Fragment>
            ))}
          </div>

          {/* R4+R5: Brother/Self + children */}
          <div className="vf-row-flex" style={{ marginTop: 25 }}>
            {bros.map((b, i) => (
              <Fragment key={b.name}>
                {(i === 1 || i === 2) && <ArrowLeft />}
                {(i === 3 || i === 4) && <ArrowRight />}
                <div className="vf-col">
                  <div className={b.center ? "lbl-m" : "lbl"}>
                    {b.lbl}
                    {b.center && <span className="req">*</span>}
                  </div>
                  <input
                    type="text"
                    name={b.name}
                    required={b.center}
                    className={`text-center ${b.center ? "mi" : ""}`}
                    style={b.center ? { fontWeight: 700 } : undefined}
                    placeholder={b.center ? "Your Name" : b.lbl.replace(/[()]/g, "")}
                  />
                  {b.center ? (
                    <svg
                      width="12"
                      height="125"
                      viewBox="0 0 12 125"
                      style={{ display: "block", margin: "0 auto", marginBottom: -4, overflow: "visible" }}
                    >
                      <line x1="6" y1="0" x2="6" y2="120" stroke="#aaa" strokeWidth="1.5" />
                      <polygon points="1,120 6,128 11,120" fill="#aaa" />
                    </svg>
                  ) : (
                    <Connector2 name1={b.c1} name2={b.c2} />
                  )}
                </div>
              </Fragment>
            ))}
          </div>

          {/* R6+R7: Sons */}
          <Connector3 name1="son1" name2="son2" name3="son3" />
        </div>
        )}

        {/* MOBILE: stacked sections, same field names */}
        {isMobile && <MobileTree />}

        {/* FOOTER */}
        <div className="vf-footer">
          <button type="submit" className="vf-submit" disabled={isSubmitting}>
            {isSubmitting ? "Submitting..." : "Submit"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default VanshavaliForm;
